import isNil from 'lodash/isNil';
import merge from 'lodash/merge';

import constants from 'app/shared/constants/ConstantsBundle';
import queryUtils from 'app/shared/utils/queryUtils';
import searchSlugManifest from 'app/shared/utils/searchSlugManifest';

interface BuildAreaPathParams {
  areaResourceId?: string;
  searchSlug?: string;
}

interface QueryParams {
  redirect?: string;
  lat?: number | string;
  lon?: number | string;
  z?: number | string;
  border?: boolean;
  minLat?: number | string;
  minLon?: number | string;
  maxLat?: number | string;
  maxLon?: number | string;
  [key: string]: string | number | boolean | undefined;
}

interface BuildFreepassPathParams {
  pathname?: string;
  query?: QueryParams;
}

interface Area {
  minLon?: number | string;
  minLat?: number | string;
  maxLon?: number | string;
  maxLat?: number | string;
}

interface AreaInfo {
  zip: string | null;
  state: string | null;
}

interface MapData {
  zoom?: number | string;
  lat: number | string;
  lon: number | string;
  border?: boolean;
  minLat?: number | string;
  minLon?: number | string;
  maxLat?: number | string;
  maxLon?: number | string;
}

interface MapDataDifferentParams {
  data: MapData;
  q: QueryParams;
}

interface MergeMapDataParams {
  data: MapData;
  q: QueryParams;
}

const routeUtils = {
  buildAreaPath({ areaResourceId = 'new-york-ny', searchSlug = 'apartments-for-rent' }: BuildAreaPathParams): string {
    const url = '/' + areaResourceId + '/' + searchSlug;
    return url;
  },
  buildFreepassPath({ pathname = '/', query = {} }: BuildFreepassPathParams = {}): string {
    let mutableQuery = query;
    let mutablePathname = pathname;

    if (mutableQuery.redirect && typeof mutableQuery.redirect === 'string') {
      mutableQuery = merge({}, mutableQuery);
      const redirectValue = mutableQuery.redirect;
      if (typeof redirectValue === 'string') {
        mutablePathname = decodeURIComponent(redirectValue);
      }
      delete mutableQuery.redirect;
    }

    let currentUrl = routeUtils.buildPathWithQuery(mutablePathname, mutableQuery);

    if (currentUrl === '/login') {
      currentUrl = '/user/account';
    }

    // add prefix for Comet freepass login
    return '/fp' + currentUrl;
  },
  buildPathWithQuery(pathname: string, queryObj: QueryParams): string {
    return pathname + queryUtils.stringify(queryObj);
  },
  getMaloneLotIdEncodedFromUrl(url = ''): string | undefined {
    let maloneLotIdEncoded: string | undefined;

    const firstSegment = url.split('/')[1];

    const urlArray = (firstSegment || '').split('-');
    if (urlArray[urlArray.length - 1]) {
      maloneLotIdEncoded = urlArray[urlArray.length - 1];
    }
    return maloneLotIdEncoded;
  },
  getMaloneUnitFromUrl(url = ''): string | undefined {
    let maloneUnit: string | undefined;
    const tempUrl = url.split('/');

    if (tempUrl[3]) {
      // there are 3 directories in the url, like address/unit/pad
      maloneUnit = tempUrl[2];
    }
    return maloneUnit;
  },
  getResourceIdFromUrl(url: string): string | false {
    if (routeUtils.isAreaUrl(url)) {
      return url.split('/')[1];
    } else {
      return false;
    }
  },
  guessAreaInfoFromUrl({ url = '/' }: { url?: string }): AreaInfo {
    const zip = url.match((constants as { ZIP_PATTERN: RegExp }).ZIP_PATTERN);
    const state = url.match((constants as { STATE_PATTERN: RegExp }).STATE_PATTERN);

    return {
      zip: zip ? zip[1] : null,
      state: state ? state[1] : null,
    };
  },
  hasLatAndLon(query: QueryParams = {}): boolean {
    return Boolean(query.lat && query.lon);
  },
  getMapDataFromQuery(query: QueryParams = {}): MapData | null {
    if (!routeUtils.hasLatAndLon(query)) {
      return null;
    }

    return {
      lat: Number(query.lat),
      lon: Number(query.lon),
      zoom: query.z ? Number(query.z) : undefined,
      maxLat: query.maxLat ? Number(query.maxLat) : undefined,
      maxLon: query.maxLon ? Number(query.maxLon) : undefined,
      minLat: query.minLat ? Number(query.minLat) : undefined,
      minLon: query.minLon ? Number(query.minLon) : undefined,
    };
  },
  getBoundedMapDataFromQuery(query: QueryParams = {}): MapData | null {
    const mapData = routeUtils.getMapDataFromQuery(query);

    return mapData && routeUtils.hasValidMinMaxLatLon(mapData) ? mapData : null;
  },
  resolveBoundedMapData(
    mapData: MapData | null | false | undefined,
    shouldUseAreaBoundary: boolean | undefined,
    query: QueryParams = {},
  ): MapData | null | false | undefined {
    if (mapData && !shouldUseAreaBoundary && !routeUtils.hasValidMinMaxLatLon(mapData)) {
      return routeUtils.getBoundedMapDataFromQuery(query);
    }

    if (!mapData && !shouldUseAreaBoundary) {
      return routeUtils.getBoundedMapDataFromQuery(query);
    }

    return mapData;
  },
  hasFilterParams(query: QueryParams = {}): boolean {
    // Common filter parameters that indicate the URL change came from filter actions
    const filterParams = [
      'lowPrice',
      'highPrice',
      'beds',
      'baths',
      'maxCreated',
      'pets',
      'startOfAvailabilityDate',
      'endOfAvailabilityDate',
      'laundry',
      'amenities',
      'furnished',
      'propertyTypes',
      'minSqft',
      'maxSqft',
      'listingTypes',
      'keywords',
      'minPhotos',
      'includeVaguePricing',
      'isListedByOwner',
      'incomeRestricted',
      'seniorHousing',
      'studentHousing',
      'militaryHousing',
      'acceptsSection8',
      'hasSpecialOffers',
      'isAcceptingRentalApplications',
      'hideUnkAvail',
      'photos',
      'promo',
      'applicationsOk',
      'avail',
      'price',
      'sqft',
      'commuteTimeMode',
      'commuteTime',
      'commuteMode',
      'commuteLats',
      'commuteLons',
    ];

    return filterParams.some((param) => query[param] !== undefined);
  },
  hasValidMinMaxLatLon(area: Area = {}): boolean {
    if (isNil(area.minLon) || isNil(area.minLat) || isNil(area.maxLon) || isNil(area.maxLat)) {
      return false;
    }
    const minLon = Number(area.minLon);
    const minLat = Number(area.minLat);
    const maxLon = Number(area.maxLon);
    const maxLat = Number(area.maxLat);

    if (isNaN(minLon) || isNaN(minLat) || isNaN(maxLon) || isNaN(maxLat)) {
      return false;
    }
    if (minLon > 180 || minLon < -180 || maxLon > 180 || maxLon < -180) {
      return false;
    }
    if (minLat > 90 || minLat < -90 || maxLat > 90 || maxLat < -90) {
      return false;
    }
    return true;
  },
  isAreaUrl(url = ''): boolean {
    const rentFragment = 'for-rent';

    const urlWithoutQuery = url.split('?')[0];
    const urlSegments = urlWithoutQuery.split('/');

    if (urlSegments.length !== 3) {
      // valid area url format: `/{area}/{searchSlug}`
      return false;
    }

    const lastSplit = urlSegments[urlSegments.length - 1];

    if (lastSplit.indexOf(rentFragment) > -1) {
      return true;
    }

    // Check against active search slugs from manifest
    if (lastSplit in searchSlugManifest.activeSearchSlugs) {
      return true;
    }

    return false;
  },
  isBuildingUrl(url = ''): boolean {
    const urlSegments = url.split('/');
    const lastSplit = urlSegments[urlSegments.length - 1];

    return lastSplit === 'building';
  },
  isNearMeUrl(url = ''): boolean {
    const urlWithoutQuery = url.split('?')[0];
    const urlSegments = urlWithoutQuery.split('/');

    if (urlSegments.length > 2) {
      return false;
    }

    const lastSplit = urlSegments[urlSegments.length - 1];
    const containsNearMeFragment = Boolean(
      (constants as { NEAR_ME_ROUTES: Record<string, unknown> }).NEAR_ME_ROUTES[`/${lastSplit}`],
    );

    if (containsNearMeFragment) {
      return true;
    }

    return false;
  },
  isPadUrl(url = ''): boolean {
    const urlSegments = url.split('/');
    const lastSplit = urlSegments[urlSegments.length - 1];

    return lastSplit.indexOf('pad') === 0;
  },
  isPadOrBuildingUrl(url = ''): boolean {
    return this.isPadUrl(url) || this.isBuildingUrl(url);
  },
  parseFailedListingAddress(path: string): string {
    // Pathname formatted like: /1234-main-street-san-francisco-ca-94105-abcdef/building
    // Split path between any '/' characters, which generates this array:
    // ["", "1234-main-street-san-francisco-ca-94105-abcdef", "building"]
    let getAddressString = path.split('/')[1];

    // These pathnames contain unique 6 character maloneLot Id's at the end that will
    // screw up the getLatLonFromString method if it's included with the address.
    // So, let's cut off the last element. I thought I could do all this on one line, but I guess not.
    const getAddressArray = getAddressString.split('-');
    getAddressArray.pop();
    getAddressString = getAddressArray.join(' ');

    return getAddressString;
  },
  checkSchoolZoneFromPathname(id: string): boolean {
    const isInSchoolZone = /-(SCHOOL|ELEMENTARY)/i.test(id);
    return isInSchoolZone;
  },
  isMapDataDifferent({ data, q }: MapDataDifferentParams): boolean {
    if (Number(data.zoom) !== Number(q.z)) {
      return true;
    } else if (Number(data.lat) !== Number(q.lat)) {
      return true;
    } else if (Number(data.lon) !== Number(q.lon)) {
      return true;
    } else if (data.border !== q.border) {
      return true;
    } else {
      return false;
    }
  },
  mergeMapDataIntoQuery({ data, q }: MergeMapDataParams): QueryParams {
    const newQueryObj = Object.assign({}, q);

    // HPWEB-2850: Map can unexpectedly revert to original zoom / location
    // if min/max lat/ on from query params are present in URL. After getting
    // new map data, delete them from newQueryObj as they aren't needed.
    delete newQueryObj.minLat;
    delete newQueryObj.minLon;
    delete newQueryObj.maxLat;
    delete newQueryObj.maxLon;

    // should use either MapData or MapDataQueryObj, or better consolidate those so we just have 1.....
    newQueryObj.z = Number(data.zoom);
    newQueryObj.lat = Number(data.lat).toFixed(4);
    newQueryObj.lon = Number(data.lon).toFixed(4);
    if (data.border === false) {
      newQueryObj.border = false;
    }
    return newQueryObj;
  },
};

export default routeUtils;
